import type { FC, MouseEvent } from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { SettingIcon } from 'assets/svg';
import { useAppSelector } from 'features/app/hooks';
import { useTrackedInteractionAction } from 'features/analytics/hooks';
import { CallCaptionsSettingsMenuButton } from 'features/call/call-captions';
import { DialPadSettingsMenuButton } from 'features/dial-pad/components';
import { SelfViewSettingsMenuButtonContainer } from 'features/local-video/components';
import { useSelfViewHotkey } from 'features/local-video/hooks';
import { MenuIconButton, MenuPopoverOld } from 'features/menu/components';
import { usePopover } from 'features/menu/hooks';
import {
  isFaceTimeConferenceSelector,
  isZoomConferenceSelector,
} from 'features/call/call-conference/store';

export const SettingsMenuButtonContainer: FC = () => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popover = usePopover('menu-settings');
  const isZoomConference = useAppSelector(isZoomConferenceSelector);
  const isFaceTimeConference = useAppSelector(isFaceTimeConferenceSelector);

  useSelfViewHotkey();

  const showSettingsPopover = () => {
    popover.handleShowPopover({
      currentTarget: buttonRef.current,
    } as MouseEvent<HTMLButtonElement>);
  };

  const { onActionClick } = useTrackedInteractionAction({
    actionName: 'settings-menu-open',
    methodName: 'showSettingsPopover',
    eventName: 'NavbarInteraction',
  });

  return (
    <>
      <MenuIconButton
        ref={buttonRef}
        title={t('menuSettings')}
        icon={<SettingIcon />}
        isDisabled={false}
        isChecked={false}
        isCheckIconVisible={false}
        onClickHandler={() => {
          showSettingsPopover();
          onActionClick();
        }}
        aria-describedby={popover.popoverId}
        testId='settings-menu-button'
      />
      <MenuPopoverOld popover={popover}>
        {!isZoomConference && !isFaceTimeConference && (
          <DialPadSettingsMenuButton />
        )}
        <SelfViewSettingsMenuButtonContainer />
        <CallCaptionsSettingsMenuButton />
      </MenuPopoverOld>
    </>
  );
};
